import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// ── SVG chart primitives ───────────────────────────────────────────────────

function BarChart({ data, color = 'var(--j1)', height = 90 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.value), 1);
  const W = data.length * 10;
  return (
    <svg width="100%" height={height + 22} viewBox={`0 0 ${W} ${height + 22}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      {data.map((d, i) => {
        const bh = Math.max((d.value / max) * height, d.value > 0 ? 2 : 0);
        return (
          <g key={i}>
            <rect x={i * 10 + 1} y={height - bh} width={8} height={bh} fill={color} rx={1.5} opacity={0.85} />
            {d.value > 0 && (
              <text x={i * 10 + 5} y={height - bh - 2} textAnchor="middle" fontSize={3.5} fill={color} opacity={0.8}>{d.value}</text>
            )}
            <text x={i * 10 + 5} y={height + 16} textAnchor="middle" fontSize={3} fill="rgba(255,255,255,0.35)">{d.label}</text>
          </g>
        );
      })}
      {/* Baseline */}
      <line x1={0} y1={height} x2={W} y2={height} stroke="rgba(255,255,255,0.1)" strokeWidth={0.5} />
    </svg>
  );
}

function DonutChart({ segments, size = 110, thickness = 18, children }) {
  const r = (size - thickness * 2) / 2;
  const cx = size / 2, cy = size / 2;
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  let angle = -Math.PI / 2;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ display: 'block' }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={thickness} />
        {segments.filter(s => s.value > 0).map((s, i) => {
          const span = (s.value / total) * 2 * Math.PI;
          const x1 = cx + r * Math.cos(angle);
          const y1 = cy + r * Math.sin(angle);
          angle += span;
          const x2 = cx + r * Math.cos(angle - 0.001);
          const y2 = cy + r * Math.sin(angle - 0.001);
          const large = span > Math.PI ? 1 : 0;
          return (
            <path key={i} d={`M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`}
              fill="none" stroke={s.color} strokeWidth={thickness} strokeLinecap="round" />
          );
        })}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', textAlign: 'center', pointerEvents: 'none' }}>
        {children}
      </div>
    </div>
  );
}

function HBar({ label, value, max, color = 'var(--j1)' }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ fontSize: 11, color: 'var(--txt2)', maxWidth: '75%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ fontSize: 11, fontFamily: 'Share Tech Mono', color }}>{value}</span>
      </div>
      <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, color = 'var(--j1)', icon }) {
  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--brd2)', borderRadius: 6, padding: '14px 16px', flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 11, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 1, marginBottom: 6 }}>{icon} {label}</div>
      <div style={{ fontFamily: 'Orbitron', fontSize: 22, fontWeight: 800, color, marginBottom: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>{sub}</div>}
    </div>
  );
}

const STATUS_COLORS = {
  submitted: '#00d4ff', peer_reviewed: '#a855f7', sme_approved: '#ff6b35',
  approved: '#00ff88', adopted: '#ffd700', rejected: '#ef4444', in_review: '#00d4ff', pending_adoption: '#ffd700',
};
const STATUS_LABEL = {
  submitted: 'Submitted', peer_reviewed: 'Peer Reviewed', sme_approved: 'SME Approved',
  approved: 'Approved', adopted: 'Adopted', rejected: 'Rejected', in_review: 'In Review', pending_adoption: 'Pending Adoption',
};

export default function Trends() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/analytics/me')
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>LOADING ANALYTICS…</div>;
  if (!stats) return <div className="empty-state">Could not load analytics.</div>;

  const statusSegments = Object.entries(stats.byStatus || {}).map(([k, v]) => ({ label: STATUS_LABEL[k] || k, value: v, color: STATUS_COLORS[k] || '#888' }));
  const catEntries = Object.entries(stats.byCategory || {}).sort((a, b) => b[1] - a[1]);
  const maxCat = Math.max(...catEntries.map(e => e[1]), 1);

  const ptBreakdown = [
    { label: 'From contributions', value: stats.pointsFromContribs, color: 'var(--j1)' },
    { label: 'From peer reviews', value: stats.pointsFromReviews, color: '#a855f7' },
    { label: 'From recognitions', value: stats.pointsFromRecognition, color: '#ffd700' },
  ];

  const rankPct = stats.totalMembers > 1 ? Math.round(((stats.totalMembers - stats.rank) / (stats.totalMembers - 1)) * 100) : 100;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div className="page-title">MY ANALYTICS</div>
        <div className="page-sub">Personal trends, points breakdown & performance snapshot</div>
      </div>

      {/* Hero stat row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="TOTAL POINTS" value={(stats.totalPoints || 0).toLocaleString()} sub={`Tier: ${stats.tier}`} color="var(--j1)" icon="⭐" />
        <StatCard label="WEEKLY POINTS" value={(stats.weeklyPoints || 0).toLocaleString()} sub={`Team avg: ${stats.teamAvgWeekly.toLocaleString()}`} color={stats.weeklyPoints >= stats.teamAvgWeekly ? 'var(--j5)' : 'var(--j6)'} icon="📈" />
        <StatCard label="RANK" value={`#${stats.rank}`} sub={`Top ${100 - rankPct}% of ${stats.totalMembers} members`} color="var(--j4)" icon="🏆" />
        <StatCard label="CONTRIBUTIONS" value={stats.totalContributions} sub={`${stats.totalReviews} peer reviews given`} color="var(--j3)" icon="📊" />
        <StatCard label="RECOGNITIONS" value={stats.recReceived} sub={`Given: ${stats.recGiven}`} color="#ffd700" icon="🌟" />
      </div>

      {/* Points over time */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 1, marginBottom: 2 }}>POINTS EARNED — LAST 8 WEEKS</div>
            <div style={{ fontSize: 10, color: 'var(--txt3)' }}>From contributions, peer reviews & recognitions received</div>
          </div>
          {stats.weeklyPoints > 0 && (
            <div style={{ fontFamily: 'Orbitron', fontSize: 16, color: 'var(--j1)', fontWeight: 700 }}>
              {stats.weeklyPoints.toLocaleString()} <span style={{ fontSize: 10, color: 'var(--txt3)' }}>this week</span>
            </div>
          )}
        </div>
        {stats.weeklyPts.every(w => w.value === 0) ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--txt3)', fontFamily: 'Share Tech Mono', fontSize: 11 }}>
            No point activity in the last 8 weeks. Submit a contribution or review a peer to get started!
          </div>
        ) : (
          <BarChart data={stats.weeklyPts} color="var(--j1)" height={100} />
        )}
        {/* Team avg comparison line label */}
        {stats.teamAvgWeekly > 0 && (
          <div style={{ marginTop: 8, fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', display: 'flex', gap: 16 }}>
            <span style={{ color: stats.weeklyPoints >= stats.teamAvgWeekly ? 'var(--j5)' : 'var(--j6)' }}>
              {stats.weeklyPoints >= stats.teamAvgWeekly ? '▲' : '▼'} {stats.weeklyPoints >= stats.teamAvgWeekly ? 'Above' : 'Below'} team average by {Math.abs(stats.weeklyPoints - stats.teamAvgWeekly).toLocaleString()} pts
            </span>
            <span>Team avg: {stats.teamAvgWeekly.toLocaleString()} pts/week</span>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>

        {/* Contribution pipeline donut */}
        <div className="card">
          <div style={{ fontSize: 11, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 1, marginBottom: 14 }}>CONTRIBUTION PIPELINE</div>
          {stats.totalContributions === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--txt3)', fontSize: 11, fontFamily: 'Share Tech Mono' }}>No contributions yet</div>
          ) : (
            <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
              <DonutChart segments={statusSegments} size={110} thickness={16}>
                <div style={{ fontFamily: 'Orbitron', fontSize: 18, fontWeight: 800, color: 'var(--j1)' }}>{stats.totalContributions}</div>
                <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>total</div>
              </DonutChart>
              <div style={{ flex: 1 }}>
                {statusSegments.map(s => (
                  <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: 'var(--txt2)', flex: 1 }}>{s.label}</span>
                    <span style={{ fontFamily: 'Share Tech Mono', fontSize: 11, color: s.color, fontWeight: 600 }}>{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Points breakdown donut */}
        <div className="card">
          <div style={{ fontSize: 11, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 1, marginBottom: 14 }}>POINTS BREAKDOWN</div>
          {stats.totalPoints === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--txt3)', fontSize: 11, fontFamily: 'Share Tech Mono' }}>No points yet</div>
          ) : (
            <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
              <DonutChart segments={ptBreakdown} size={110} thickness={16}>
                <div style={{ fontFamily: 'Orbitron', fontSize: 16, fontWeight: 800, color: 'var(--j1)' }}>{(stats.totalPoints || 0).toLocaleString()}</div>
                <div style={{ fontSize: 8, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>total pts</div>
              </DonutChart>
              <div style={{ flex: 1 }}>
                {ptBreakdown.map(s => (
                  <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: 'var(--txt2)', flex: 1 }}>{s.label}</span>
                    <span style={{ fontFamily: 'Share Tech Mono', fontSize: 11, color: s.color, fontWeight: 600 }}>{s.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Category breakdown */}
      {catEntries.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 1, marginBottom: 14 }}>CONTRIBUTIONS BY CATEGORY</div>
          {catEntries.map(([cat, count], i) => (
            <HBar key={cat} label={cat} value={count} max={maxCat} color={i === 0 ? 'var(--j1)' : i === 1 ? '#a855f7' : i === 2 ? 'var(--j5)' : 'var(--txt2)'} />
          ))}
        </div>
      )}

      {/* Rank visualization */}
      <div className="card">
        <div style={{ fontSize: 11, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 1, marginBottom: 14 }}>LEADERBOARD POSITION</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ height: 10, background: 'rgba(255,255,255,0.06)', borderRadius: 5, overflow: 'hidden', marginBottom: 6 }}>
              <div style={{ height: '100%', width: `${rankPct}%`, background: 'linear-gradient(90deg, var(--j6), var(--j4), var(--j5))', borderRadius: 5, transition: 'width 0.8s ease' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>
              <span>Last place</span><span>Top 25%</span><span>Top 10%</span><span>1st</span>
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontFamily: 'Orbitron', fontSize: 28, fontWeight: 900, color: 'var(--j4)', lineHeight: 1 }}>#{stats.rank}</div>
            <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>of {stats.totalMembers}</div>
          </div>
        </div>
        <div style={{ marginTop: 14, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div><div style={{ fontFamily: 'Orbitron', fontSize: 14, color: 'var(--j1)' }}>{stats.totalPoints.toLocaleString()}</div><div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>Total Points</div></div>
          <div><div style={{ fontFamily: 'Orbitron', fontSize: 14, color: '#a855f7' }}>{stats.totalReviews}</div><div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>Reviews Given</div></div>
          <div><div style={{ fontFamily: 'Orbitron', fontSize: 14, color: '#ffd700' }}>{stats.recReceived}</div><div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>Times Recognised</div></div>
          <div><div style={{ fontFamily: 'Orbitron', fontSize: 14, color: 'var(--j5)' }}>{stats.totalContributions}</div><div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono' }}>Contributions</div></div>
        </div>
      </div>
    </div>
  );
}
